import { useState, MouseEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

// material-ui
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

// assets
import { Buildings2, Card, Edit2, Logout, Profile } from 'iconsax-reactjs';

// ==============================|| HEADER PROFILE - PROFILE TAB ||============================== //
//
// USER-03 (VEK-256) — every row opens a Vektra page: the template routes this tab shipped with
// (/apps/profiles/…, /apps/invoice/…) were never registered and landed on the 404 page. The former
// "Social profile" row (index 3) is not rendered; its key is `headerProfile.businessAccount` and the
// business-account page (VEK-257) re-adds the row with it.

interface Props {
  handleLogout: () => void;
}

// ACCT-03 (VEK-257) — the Business account row is back, opening the Profile page where the Company card sits.
const ROUTES = {
  editProfile: '/profile/edit',
  viewProfile: '/profile',
  businessAccount: '/profile',
  billing: '/billing'
} as const;

export default function ProfileTab({ handleLogout }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const [selectedIndex, setSelectedIndex] = useState<number>();
  const handleListItemClick = (event: MouseEvent<HTMLDivElement>, index: number, route: string = '') => {
    setSelectedIndex(index);

    if (route && route !== '') {
      navigate(route);
    }
  };

  useEffect(() => {
    const pathToIndex: { [key: string]: number } = {
      [ROUTES.editProfile]: 0,
      [ROUTES.viewProfile]: 1,
      [ROUTES.billing]: 4
    };

    setSelectedIndex(pathToIndex[pathname] ?? undefined);
  }, [pathname]);

  return (
    <List component="nav" sx={{ p: 0, '& .MuiListItemIcon-root': { minWidth: 32 } }} data-testid="header-profile-tab">
      <ListItemButton
        selected={selectedIndex === 0}
        onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 0, ROUTES.editProfile)}
        data-testid="header-profile-edit"
      >
        <ListItemIcon>
          <Edit2 variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={t('headerProfile.editProfile')} />
      </ListItemButton>
      <ListItemButton
        selected={selectedIndex === 1}
        onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 1, ROUTES.viewProfile)}
        data-testid="header-profile-view"
      >
        <ListItemIcon>
          <Profile variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={t('headerProfile.viewProfile')} />
      </ListItemButton>
      <ListItemButton
        selected={selectedIndex === 3}
        onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 3, ROUTES.businessAccount)}
        data-testid="header-profile-business-account"
      >
        <ListItemIcon>
          <Buildings2 variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={t('headerProfile.businessAccount')} />
      </ListItemButton>
      <ListItemButton
        selected={selectedIndex === 4}
        onClick={(event: MouseEvent<HTMLDivElement>) => handleListItemClick(event, 4, ROUTES.billing)}
        data-testid="header-profile-billing"
      >
        <ListItemIcon>
          <Card variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={t('headerProfile.billing')} />
      </ListItemButton>
      <ListItemButton selected={selectedIndex === 2} onClick={handleLogout} data-testid="header-profile-logout">
        <ListItemIcon>
          <Logout variant="Bulk" size={18} />
        </ListItemIcon>
        <ListItemText primary={t('headerProfile.logout')} />
      </ListItemButton>
    </List>
  );
}
